import { JSX } from 'react';
import { Layout } from '../../components/core';
import { PageContent, PageError, PageFooter, PageHeader, PageLoading } from '../../components/page';
import { SelfServiceCards } from '../../components/recommendations/SelfServiceCards';
import { Portals } from '../../content/portal/portals';
import { usePortalContext } from '../../contexts/portalContext';
import { CoachingProgramType, RecommendationOptions } from '../../graphQL';
import { getStylesheet } from '../../styles';
import { useTitle } from '../../utils/useTitle';
import {
  CustomClinicalRecCTACard,
  EmotionalWellnessCoachingCTACard,
  ExecutiveFunctionCoachingCTACard,
  SelfCareCTACard,
  TherapyCTACard,
} from './components/CareRecommendationCTACards';
import {
  CareRecommendationCrisisFeedback,
  CoachingSelfCareFeedback,
} from './components/CareRecommendationStatusFeedbacks';
import { useCareRecommendation } from './hooks/useCareRecommendation';

const { Coaching, CustomClinicalRec, Therapy } = RecommendationOptions;

export const RecommendationPage = (): JSX.Element => {
  useTitle('Your recommendation');

  const [careRecData, { loading, error }] = useCareRecommendation();
  const { onlyIfPortalIsActive } = usePortalContext();

  const skillsPortalActive = onlyIfPortalIsActive(Portals.Skills, true, false);

  if (loading) {
    return <PageLoading pageName="recommendation" />;
  }
  if (error) {
    return <PageError />;
  }

  const { careRecommendation } = careRecData;

  return (
    <PageContent testID="recommendation-page">
      <Layout.Box {...styles.topStack}>
        <PageHeader
          title="Your personalized care recommendation"
          undertext="Based on your answers, we have tailored care recommendations that best support your mental health care journey."
          testID="recommendation-header"
          paddingBottom={0}
        />

        {careRecommendation === Coaching && (
          <CoachingCards coachingProgramOptions={careRecData.coachingProgramOptions} />
        )}
        {careRecommendation === CustomClinicalRec && <CustomClinicCards />}
        {careRecommendation === Therapy && (
          <TherapyCards isUserDeactivated={careRecData.isUserDeactivated ?? false} />
        )}

        <SelfServiceCards
          showConnectNow={careRecData.connectNowEnabled}
          showTogetherall={careRecData.togetherAllEnabled}
          showSelfCare={careRecommendation !== Coaching && skillsPortalActive}
        />
      </Layout.Box>

      <PageFooter />
    </PageContent>
  );
};

const styles = getStylesheet({
  topStack: {
    gap: 8,
  },
});

const coachingProgramTypeCTACardMap: Record<CoachingProgramType, () => JSX.Element> = {
  [CoachingProgramType.ExecutiveFunctioning]: ExecutiveFunctionCoachingCTACard,
  [CoachingProgramType.EmotionalWellness]: EmotionalWellnessCoachingCTACard,
};

type CoachingCardsProps = {
  coachingProgramOptions?: CoachingProgramType[];
};

const CoachingCards = ({ coachingProgramOptions }: CoachingCardsProps): JSX.Element => {
  return (
    <>
      {coachingProgramOptions?.map(programType => {
        const Component = coachingProgramTypeCTACardMap[programType];
        return <Component key={programType} />;
      })}

      <SelfCareCTACard key="self-care" />

      <CoachingSelfCareFeedback key="coaching-self-care-feedback" />
    </>
  );
};

const CustomClinicCards = (): JSX.Element => {
  return (
    <>
      <CareRecommendationCrisisFeedback key="custom-clinical-rec-status-feedback" />

      <CustomClinicalRecCTACard key="custom-clinical-rec" />
    </>
  );
};

const TherapyCards = ({ isUserDeactivated }: { isUserDeactivated: boolean }): JSX.Element => {
  return <TherapyCTACard key="therapy" isUserDeactivated={isUserDeactivated} />;
};
